import React, { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import { exportToPDF, exportToWord, exportToText } from '../../utils/reportExport';
import logger from '../../utils/logger';
import { LifestylePlanForm } from './lifestyle/LifestylePlanForm';
import { LifestylePlanResults } from './lifestyle/LifestylePlanResults';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AILifestylePlanner = ({ patientId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [plan, setPlan] = useState(null);
  const [showWearables, setShowWearables] = useState(false);
  const [wearableData, setWearableData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [expandedSections, setExpandedSections] = useState({ diet: true, workout: true, yoga: true, schedule: false });

  const [formData, setFormData] = useState({
    focus_areas: ['general_wellness'], plan_duration: '4_weeks', activity_level: 'moderate',
    age: '', gender: 'male', current_weight_kg: '', target_weight_kg: '',
    dietary_preferences: [], health_conditions: [], allergies: []
  });

  useEffect(() => { fetchWearableData(); }, [patientId]); // eslint-disable-line

  const fetchWearableData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/wearable-integrations/lifestyle-data/${patientId || 'demo_user'}`);
      if (response.ok) {
        const data = await response.json();
        if (data.connected_devices > 0) {
          setWearableData(data);
          if (data.ai_ready_data) {
            setFormData(prev => ({
              ...prev,
              activity_level: data.ai_ready_data.activity_level || prev.activity_level,
              focus_areas: data.focus_area_suggestions || prev.focus_areas
            }));
          }
        }
      }
    } catch { logger.log('No wearable data available'); }
  };

  const generatePlan = async () => {
    setLoading(true); setError(null);
    try {
      const response = await fetch(`${API_URL}/api/ai-wellness/generate-plan`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_id: patientId || 'demo_patient', ...formData,
          age: formData.age ? parseInt(formData.age) : null,
          current_weight_kg: formData.current_weight_kg ? parseFloat(formData.current_weight_kg) : null,
          target_weight_kg: formData.target_weight_kg ? parseFloat(formData.target_weight_kg) : null
        })
      });
      if (!response.ok) throw new Error('Failed to generate plan');
      setPlan(await response.json());
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const toggleSection = (section) => setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  const toggleFocusArea = (areaId) => setFormData(prev => ({
    ...prev, focus_areas: prev.focus_areas.includes(areaId) ? prev.focus_areas.filter(id => id !== areaId) : [...prev.focus_areas, areaId]
  }));

  const handleExportPDF = async () => {
    setExporting(true);
    try { await exportToPDF(plan, { name: patientId || 'Patient' }); }
    catch (err) { logger.error('PDF export error:', err); }
    finally { setExporting(false); }
  };

  const handleExportWord = () => exportToWord(plan, { name: patientId || 'Patient' });

  const handlePrint = () => {
    const content = exportToText(plan, { name: patientId || 'Patient' });
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.open();
    const doc = printWindow.document;
    const root = doc.createElement('html');
    root.innerHTML = `<head><title>Infuse Wellness Plan</title><style>body{font-family:'Courier New',monospace;white-space:pre-wrap;padding:20px}</style></head><body>${content}</body>`;
    doc.replaceChild(root, doc.documentElement);
    doc.close(); printWindow.print();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 text-white p-6" data-testid="ai-lifestyle-planner">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 rounded-full mb-4">
            <Sparkles className="w-5 h-5 text-emerald-400" /><span className="text-emerald-400 font-medium">AI-Powered</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Lifestyle Correction Plan</h1>
          <p className="text-slate-400 max-w-2xl mx-auto">Get a personalized diet, workout, and yoga plan based on your health goals. Includes YouTube video demonstrations for yoga asanas.</p>
        </div>

        {!plan && (
          <LifestylePlanForm formData={formData} setFormData={setFormData} loading={loading} error={error}
            showWearables={showWearables} setShowWearables={setShowWearables} wearableData={wearableData}
            patientId={patientId} fetchWearableData={fetchWearableData} generatePlan={generatePlan} toggleFocusArea={toggleFocusArea} />
        )}

        {plan && (
          <LifestylePlanResults plan={plan} expandedSections={expandedSections} toggleSection={toggleSection}
            handleExportPDF={handleExportPDF} handleExportWord={handleExportWord} handlePrint={handlePrint}
            exporting={exporting} onNewPlan={() => setPlan(null)} />
        )}
      </div>
    </div>
  );
};

export default AILifestylePlanner;
